import { Box, ButtonBase, Checkbox } from '@mui/material';
import classNames from 'classnames';
import Typography from '../../../../components/Typography/Typography';
import { FeatureFlagEnum } from '../../../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import UpdateConditionsAndPricesWarning from '../../components/UpdateConditionsAndPricesWarning';
import { useOrderContext } from '../../contexts/Order.context';
import useUpdateOrderModal from '../hooks/useUpdateOrderModal';
import useUpdateOrderModalStyles from '../styles/useUpdateOrderModalStyles';
import ForceUpdateBaseModal from './ForceUpdateBaseModal';

type UpdateOrderModalProps = {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onCloseWithAlreadySeenUpdate: () => void;
  isOrderOutdated: boolean;
  isFinishingOrder: boolean;
  handleConfirmFinishOrder: () => void;
  resetHasAlreadySeenUpdate: () => void;
};

const UpdateOrderModal = ({
  isOpen,
  orderId,
  onClose,
  onCloseWithAlreadySeenUpdate,
  isOrderOutdated,
  isFinishingOrder,
  handleConfirmFinishOrder,
  resetHasAlreadySeenUpdate,
}: UpdateOrderModalProps) => {
  const { classes } = useUpdateOrderModalStyles();
  const { translate } = useTranslation();
  const { getFeatureFlag } = useFeatureFlag();
  const { order } = useOrderContext();
  const {
    mustUpdatePrices,
    mustUpdateConditions,
    handleUpdateOrder,
    onClickPricesCheckbox,
    onClickConditionsCheckbox,
  } = useUpdateOrderModal({ orderId, onClose });

  const isUpdatePricesEnabled = getFeatureFlag(FeatureFlagEnum.ENABLE_UPDATE_PRICES);
  const isForceUpdateConditionsEnabled = getFeatureFlag(
    FeatureFlagEnum.ENABLE_FORCE_UPDATE_ORDER_CONDITIONS,
  );

  const onAccept = () => {
    resetHasAlreadySeenUpdate();
    handleUpdateOrder();
  };

  const handleCloseWithFinish = () => {
    if (isFinishingOrder) {
      return handleConfirmFinishOrder();
    }
    return onCloseWithAlreadySeenUpdate();
  };

  return (
    <ForceUpdateBaseModal
      isOpen={isOpen}
      onAccept={onAccept}
      onDeny={handleCloseWithFinish}
      title={LangEnum.GLOBAL_UPDATE_ORDER}
      onClose={onCloseWithAlreadySeenUpdate}
      acceptLabel={LangEnum.GLOBAL_UPDATE_LABEL}
      disablePrimaryAction={!mustUpdatePrices && !mustUpdateConditions}
      denyLabel={isFinishingOrder ? LangEnum.ORDER_ITEMS_FINISH_MESSAGE : LangEnum.GLOBAL_GO_BACK}
      content={
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
          }}
        >
          {isOrderOutdated && (
            <Typography color="textSecondary" gutterBottom>
              {translate(LangEnum.ORDER_UPDATE_MODAL_DESCRIPTION)}
            </Typography>
          )}

          <Typography variant="overline1-regular" color="textSecondary">
            {translate(LangEnum.ORDER_UPDATE_MODAL_WANT_TO_MAKE_ANY_UPDATES)}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: '4px',
            }}
          >
            {isUpdatePricesEnabled && (
              <ButtonBase
                onClick={onClickPricesCheckbox}
                className={classNames({
                  [classes.checkboxLine]: true,
                  [classes.selectedCheckboxLine]: mustUpdatePrices,
                })}
              >
                <Checkbox color="primary" checked={mustUpdatePrices} />
                <Typography color="textPrimary">
                  {translate(LangEnum.GLOBAL_ITEM_PRICES)}
                </Typography>
              </ButtonBase>
            )}
            {isForceUpdateConditionsEnabled && !order?.splitted && (
              <ButtonBase
                onClick={onClickConditionsCheckbox}
                className={classNames({
                  [classes.checkboxLine]: true,
                  [classes.selectedCheckboxLine]: mustUpdateConditions,
                })}
              >
                <Checkbox color="primary" checked={mustUpdateConditions} />
                <Typography color="textPrimary">
                  {translate(LangEnum.GLOBAL_ORDER_SPLITS)}
                </Typography>
              </ButtonBase>
            )}
          </Box>

          <UpdateConditionsAndPricesWarning />
        </Box>
      }
    />
  );
};

export default UpdateOrderModal;
